import { FC } from "react"

import { Box, Divider, Stack, Typography } from "@mui/material"
import { ITeacherStorePurchaseResult } from "api/reactQuery/mutations/classesStorePurchase.types"
import { Trans, useTranslation } from "react-i18next"
import { showDecimal } from "utils/decimalNumber"

import { DoneIcon, Notice } from "../SendStoreChargeDialog.parts"
import { roundToCents } from "../SendStoreChargeDialog.utils"

const boldSpan = <Box component="span" fontWeight={700} />

interface IProps {
  result: ITeacherStorePurchaseResult
  costPerStudent: number
}

const DoneStep: FC<IProps> = ({ result, costPerStudent }) => {
  const { t } = useTranslation()
  const chargedCount = result.charged_student_ids.length
  const skippedCount = result.skipped_student_ids.length

  return (
    <Stack alignItems="center" textAlign="center" gap={3}>
      <DoneIcon tone={chargedCount > 0 ? "success" : "warning"} />
      {chargedCount > 0 ? (
        <Stack gap={1}>
          <Typography variant="h2" fontWeight={700} color="mockup.neutral10">
            {t("storeCharge.done.title")}
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={500}
            color="mockup.neutral10"
          >
            <Trans
              i18nKey="storeCharge.done.summary"
              count={chargedCount}
              values={{
                count: chargedCount,
                perStudent: showDecimal(costPerStudent),
                total: showDecimal(roundToCents(costPerStudent * chargedCount)),
              }}
              components={{ b: boldSpan }}
            />
          </Typography>
          <Typography variant="body1" color="mockup.neutral20">
            {t("storeCharge.done.inventoryUpdated")}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="subtitle1" color="mockup.neutral10">
          {t("storeCharge.done.noneBody")}
        </Typography>
      )}
      {chargedCount > 0 && skippedCount > 0 && (
        <>
          <Divider flexItem sx={{ borderColor: "mockup.primary95" }} />
          <Notice alignSelf="stretch" tone="warning">
            <Typography
              variant="body1"
              fontWeight={500}
              color="mockup.neutral10"
            >
              <Trans
                i18nKey="storeCharge.done.skipped"
                count={skippedCount}
                values={{ count: skippedCount }}
                components={{ b: boldSpan }}
              />
            </Typography>
          </Notice>
        </>
      )}
    </Stack>
  )
}

export default DoneStep
